/**
 * My profile — who you are here, and what the system holds on you.
 * Route: /profile
 * @file src/web/features/main/pages/profile.tsx
 *
 * @llm-rule WHEN: Someone opens "My profile" from the account menu
 * @llm-rule NOTE: This replaced the scaffold's generic account page, which
 *   rendered UIKit's own "MyApp" chrome inside a Presynce session and showed
 *   the AppKit role ("admin.org") rather than the rank people actually have.
 * @llm-rule AVOID: Adding edit controls for rank, team or manager — those are
 *   set by a Company Admin or Head on the Company screen, never by the person themselves.
 */

import { useCallback, useEffect, useState } from 'react';
import { Button, Skeleton, toast } from '@bloomneo/uikit';
import { Building2, KeyRound, Mail, MapPin, Phone, ScanFace, UserCog } from 'lucide-react';
import { AuthGuard, useAuth } from '../../auth';
import { AppShell } from '../../../shared/components/AppShell';
import { Panel, PersonAvatar, SectionLabel } from '../../../shared/components/presence';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import { ROLE_LABEL, type OrgRole } from '../../../shared/domain';

interface Office {
  id: string;
  name: string;
  address?: string | null;
  radiusM: number;
}

interface Me {
  id: string;
  name: string | null;
  email: string;
  phone: string | null;
  orgRole: OrgRole;
  title: string | null;
  initials: string | null;
  manager?: { name: string | null; orgRole: OrgRole } | null;
  team?: { name: string } | null;
  department?: { name: string } | null;
}

function when(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/* -------------------------------------------------------------------------- */
/* A labelled fact. MODULE level.                                             */
/* -------------------------------------------------------------------------- */

function Fact({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Mail;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 size-4 flex-none text-ink-ghost" strokeWidth={1.8} />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.08em] text-ink-ghost">{label}</div>
        <div className="mt-0.5 truncate text-[13.5px] text-ink">{value || '—'}</div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function ProfilePage() {
  const { request } = useAppRequest();
  const { user } = useAuth();

  const [me, setMe] = useState<Me | null>(null);
  const [face, setFace] = useState<{ image: string | null; enrolledAt: string | null } | null>(null);
  const [offices, setOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [context, faceResult] = await Promise.all([
      request<{ context: { person: Me; offices: Office[] } }>('/api/attendance/context'),
      request<{ image: string | null; enrolledAt: string | null }>('/api/attendance/face/me'),
    ]);

    if (context.ok) {
      setMe(context.data.context.person);
      setOffices(context.data.context.offices ?? []);
    } else {
      toast.error(context.error);
    }

    if (faceResult.ok) setFace(faceResult.data);
    setLoading(false);
  }, [request]);

  useEffect(() => {
    void load();
  }, [load]);

  const person = me ?? {
    id: user?.id ?? '',
    name: user?.name ?? null,
    email: user?.email ?? '',
    phone: null,
    orgRole: (user?.orgRole as OrgRole) ?? 'EMPLOYEE',
    title: null,
    initials: null,
  };

  return (
    <AppShell
      wide
      title="My profile"
      subtitle="Your details, the face on your account, and where you are expected to clock in. Rank and reporting line are set by your Company Admin or Head."
    >
      {loading ? (
        <Skeleton className="h-80 w-full rounded-[18px]" />
      ) : (
        <div className="mx-auto flex max-w-3xl flex-col gap-4">
          {/* ------------------------------ Identity ----------------------------- */}
          <Panel className="flex flex-col gap-5">
            <div className="flex items-center gap-4">
              <PersonAvatar person={person} size={58} />
              <div className="min-w-0">
                <h2 className="truncate text-[20px] font-semibold tracking-tight text-ink">
                  {person.name}
                </h2>
                <p className="truncate text-[13px] text-ink-soft">
                  {person.title || ROLE_LABEL[person.orgRole]}
                </p>
                <span
                  className="mt-1.5 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold text-white"
                  style={{ background: 'var(--brand-ramp)' }}
                >
                  {ROLE_LABEL[person.orgRole]}
                </span>
              </div>
            </div>

            <div className="grid gap-4 border-t border-rule pt-4 sm:grid-cols-2">
              <Fact icon={Mail} label="Email" value={person.email} />
              <Fact icon={Phone} label="Phone" value={person.phone} />
              <Fact
                icon={UserCog}
                label="Reports to"
                value={
                  me?.manager
                    ? `${me.manager.name} · ${ROLE_LABEL[me.manager.orgRole]}`
                    : 'Nobody — you are at the top'
                }
              />
              <Fact
                icon={Building2}
                label="Team"
                value={
                  [me?.team?.name, me?.department?.name].filter(Boolean).join(' · ') || 'Company-wide'
                }
              />
            </div>
          </Panel>

          {/* ---------------------------- Face on file --------------------------- */}
          <Panel className="flex flex-col gap-3.5">
            <SectionLabel>Face on your account</SectionLabel>

            {face?.enrolledAt ? (
              <div className="flex items-start gap-4">
                {face.image ? (
                  <img
                    src={face.image}
                    alt="Your enrolment photo"
                    className="size-24 flex-none rounded-2xl border border-rule object-cover"
                    style={{ boxShadow: 'var(--elev-1)' }}
                  />
                ) : (
                  <div className="grid size-24 flex-none place-items-center rounded-2xl border border-dashed border-rule bg-paper-sunk">
                    <ScanFace className="size-7 text-ink-ghost" strokeWidth={1.4} />
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <p className="text-[13px] leading-relaxed text-ink">
                    Enrolled on {when(face.enrolledAt)}. Every clock-in is matched against this
                    photo.
                  </p>
                  <p className="mt-1.5 text-[12px] leading-relaxed text-ink-soft">
                    You can only enrol once. If it needs redoing — new glasses, a poor first capture
                    — ask your Company Admin or Head to reset it, and you’ll enrol again at your next clock-in.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3 rounded-xl bg-paper-sunk px-3.5 py-3">
                <ScanFace className="mt-0.5 size-5 flex-none text-ink-faint" strokeWidth={1.6} />
                <p className="text-[12.5px] leading-relaxed text-ink-soft">
                  No face enrolled yet. You’ll be asked to enrol the first time you open the
                  clock-in screen, and that photo becomes your reference.
                </p>
              </div>
            )}
          </Panel>

          {/* --------------------------- Where you clock in ----------------------- */}
          <Panel className="flex flex-col gap-3">
            <SectionLabel>Where you clock in</SectionLabel>

            {offices.length === 0 ? (
              <p className="text-[12.5px] leading-relaxed text-ink-soft">
                No office applies to you yet, so every clock-in counts as off-site and needs a
                reason. Your Company Admin or Head sets this on the Offices screen.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {offices.map((office) => (
                  <li
                    key={office.id}
                    className="flex items-start gap-3 rounded-xl bg-paper-sunk px-3.5 py-3"
                  >
                    <MapPin className="mt-0.5 size-4 flex-none text-brand" strokeWidth={1.9} />
                    <div className="min-w-0">
                      <div className="truncate text-[13px] font-medium text-ink">{office.name}</div>
                      {office.address && (
                        <div className="truncate text-[11.5px] text-ink-faint">{office.address}</div>
                      )}
                      <div className="tnum mt-0.5 text-[11.5px] text-ink-faint">
                        Anywhere within {office.radiusM} m counts as on-site.
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          {/* ------------------------------ Password ----------------------------- */}
          <Panel className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <KeyRound className="mt-0.5 size-4 flex-none text-ink-ghost" strokeWidth={1.8} />
              <div>
                <div className="text-[13px] font-medium text-ink">Password</div>
                <div className="text-[12px] text-ink-soft">
                  Change it from the account menu. Someone above you can issue a new one if you are
                  locked out.
                </div>
              </div>
            </div>

            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() =>
                toast.info('Open the avatar menu at the top right and choose “Change password”.')
              }
            >
              Change password
            </Button>
          </Panel>
        </div>
      )}
    </AppShell>
  );
}

export default function ProfileRoute() {
  return (
    <AuthGuard>
      <ProfilePage />
    </AuthGuard>
  );
}
